import { Home, MapPin, Video } from 'lucide-react';

import type { HubOption, LocationKind } from './types';

/**
 * Canonical kind -> lucide icon map. One source of truth so every location
 * surface (editor, library, slot block, activity chips) renders the same glyph
 * for venue / at_home / online and can never drift apart.
 */
export const KIND_ICON: Record<LocationKind, typeof MapPin> = {
  venue: MapPin,
  at_home: Home,
  online: Video
};

/** Host of a URL (e.g. "zoom.us"), falling back to the raw string for non-URLs. */
export function hostOf(url: string): string {
  try {
    return new URL(url).host || url;
  } catch {
    return url;
  }
}

/** Travel radius in whole kilometers (meters / 1000, rounded). */
export function radiusToKm(meters: number): number {
  return Math.round(meters / 1000);
}

/** Travel radius formatted as a "N km" string. */
export function formatRadiusKm(meters: number): string {
  return `${radiusToKm(meters)} km`;
}

/**
 * A place's street and city on one line, with the postcode when known: "10 rue de Rivoli, 75004 Paris".
 * With `details`, the address details after the street; with `country`, the country last (as given).
 */
export function formatStreetAndCity(
  place: {
    addressLine1?: string | null;
    addressLine2?: string | null;
    zipCode?: string | null;
    city?: string | null;
    country?: string | null;
  },
  { details = false, country = false }: { details?: boolean; country?: boolean } = {}
): string {
  return [
    place.addressLine1,
    details && place.addressLine2,
    [place.zipCode, place.city].filter(Boolean).join(' '),
    country && place.country
  ]
    .filter(Boolean)
    .join(', ');
}

/** A place at a Hub: "Studio Rivoli · 10 rue de Rivoli, Paris"; the place alone when there is no Hub. */
export function joinHubPlace(hubName: string | null | undefined, place: string | null | undefined): string {
  return [hubName, place].filter(Boolean).join(' · ');
}

/**
 * Whether a Hub location's label is its own: not empty, and not the Hub's name again with case and
 * surrounding spaces aside (the server stores no label then, and readers name the Hub).
 */
export function hasOwnHubLabel(label: string | null | undefined, hubName: string): boolean {
  const own = label?.trim();
  return !!own && own.toLocaleLowerCase() !== hubName.trim().toLocaleLowerCase();
}

/** A Hub as offered in the location editor: "Studio Rivoli · 10 rue de Rivoli, Paris". */
export function formatHubOption(
  hub: Pick<HubOption, 'name'> & Partial<Pick<HubOption, 'addressLine1' | 'city'>>
): string {
  return joinHubPlace(hub.name, formatStreetAndCity(hub));
}
